import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { DollarSign, Users, Film, Play, Clock, TrendingUp } from 'lucide-react';
import type { UserCostReport } from './userCostAnalysisService';

function formatBRL(value: number) {
  return value.toLocaleString(getInterfaceLanguage(), { style: 'currency', currency: 'BRL' });
}

function formatUSD(value: number) {
  return `USD ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatNumber(value: number) {
  return value.toLocaleString(getInterfaceLanguage());
}

interface UserCostSummaryProps {
  report: UserCostReport | null;
  loading: boolean;
}

export function UserCostSummary({ report, loading }: UserCostSummaryProps) {
  useLanguage();
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm animate-pulse">
            <div className="h-4 bg-slate-100 rounded w-24 mb-3" />
            <div className="h-8 bg-slate-100 rounded w-32" />
          </div>
        ))}
      </div>
    );
  }

  if (!report) return null;

  const { summary } = report;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-1.5 mb-2">
          <DollarSign size={14} className="text-blue-500" />
          <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Custo Total Plataforma")}</span>
        </div>
        <p className="text-xl font-bold text-slate-900">{formatBRL(summary.totalPlatformCostBRL)}</p>
        <p className="text-[11px] text-slate-400 mt-1">{formatUSD(summary.totalPlatformCostUSD)}</p>
      </div>

      <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-1.5 mb-2">
          <TrendingUp size={14} className="text-emerald-500" />
          <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Custo Usuários")}</span>
        </div>
        <p className="text-xl font-bold text-slate-900">{formatBRL(summary.totalUserCostsBRL)}</p>
        <p className="text-[11px] text-slate-400 mt-1">{formatUSD(summary.totalUserCostsUSD)}</p>
      </div>

      <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-1.5 mb-2">
          <DollarSign size={14} className="text-amber-500" />
          <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Custo Médio/Usuário")}</span>
        </div>
        <p className="text-xl font-bold text-slate-900">{formatBRL(summary.averageCostPerUserBRL)}</p>
        <p className="text-[11px] text-slate-400 mt-1">{formatUSD(summary.averageCostPerUserUSD)}</p>
      </div>

      <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-1.5 mb-2">
          <Users size={14} className="text-indigo-500" />
          <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Total Usuários")}</span>
        </div>
        <p className="text-xl font-bold text-slate-900">{formatNumber(summary.totalUsers)}</p>
        <p className="text-[11px] text-slate-400 mt-1">{translateInterfaceText("Usuários ativos no período")}</p>
      </div>

      <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-1.5 mb-2">
          <Film size={14} className="text-purple-500" />
          <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Total Vídeos")}</span>
        </div>
        <p className="text-xl font-bold text-slate-900">{formatNumber(summary.totalVideos)}</p>
        <p className="text-[11px] text-slate-400 mt-1">{translateInterfaceText("Vídeos na plataforma")}</p>
      </div>

      <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-1.5 mb-2">
          <Play size={14} className="text-cyan-500" />
          <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Total Plays")}</span>
        </div>
        <p className="text-xl font-bold text-slate-900">{formatNumber(summary.totalPlays)}</p>
        <p className="text-[11px] text-slate-400 mt-1">{translateInterfaceText("Reproduções no período")}</p>
      </div>
    </div>
  );
}

export default UserCostSummary;
